import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import PreviewTermsConditions from "../../../shared/previewTermsConditions/PreviewTermsConditions";
import styles from "./AcceptTermsAndConditions.module.scss";
import { Grid, Box } from "@mui/material";
import { TERMS_AND_CONDITIONS } from "../../../common/Constants";
import PendingActionPrompt from "../../../shared/pendingActionPrompt/PendingActionPrompt";
import ConfirmDialog from "../../../shared/confirmDialog/ConfirmDialog";
import { withTranslation } from "react-i18next";
import HasPermissionTo from "../../../common/HasPermissionTo";
import TermsAndConditionsClient from "../../../clients/TermsAndConditionsClient";
import PendingActionClient from "../../../clients/PendingActionClient";
import { IS_CANADA_MARKET } from "../../../utils/EmpUtil";
import UpperCaseText from "../../../utils/UpperCaseText";
import EMPButton from "../../../shared/components/EMPButton/EMPButton";
import MotionChevronLeft from "../../../shared/Icons/MotionChevronLeft";

const AcceptTermsAndConditions = (props) => {
  const [openDeclinePAPrompt, setOpenDeclinePAPrompt] = useState(false);
  const [openConfirmPAPrompt, setOpenConfirmPAPrompt] = useState(false);
  const [openDeclineDialog, setOpenDeclineDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [dealersActioned, setDealersActioned] = useState([]);
  const [dealersNotActioned, setDealersNotActioned] = useState([]);
  const [tnCStatus, setTnCStatus] = useState({});

  const termsAndConditions =
    props.termsAndConditions || props.termsAndConditionsHistory[0];
  const onboardedDealerGroup = props.user.getOnboardedDealerGroup(true);

  const customText = useMemo(
    () => ({
      actioned: props.t("PendingActionPrompt.actioned"),
      notActioned: props.t("PendingActionPrompt.notActioned"),
      applySelective: props.t("PendingActionPrompt.applySelectiveTerms"),
    }),
    [props]
  );

  const deactivateDialogProps = useMemo(
    () => ({
      title: props.t("BusinessSettings.titleDecline"),
      bodyText: props.user.isLoggedInUserMDLR
        ? props.t("BusinessSettings.bodyTextDeclineWarning")
        : props.t("BusinessSettings.bodyTextDecline"),
      confirm: props.t("BusinessSettings.confirm"),
      cancel: props.t("BusinessSettings.cancel"),
    }),
    [props]
  );

  const confirmDialogProps = useMemo(
    () => ({
      title: props.t("BusinessSettings.titleAccept"),
      bodyText: props.t("BusinessSettings.bodyTextAcceptWarning"),
      confirm: props.t("BusinessSettings.confirm"),
      cancel: props.t("BusinessSettings.cancel"),
    }),
    [props]
  );

  const dealerList = useMemo(() => {
    const commonIds = props.user.getCommonIdsWithoutSDLR().split(",");
    commonIds.forEach((commonId, index) => {
      commonIds[index] = props.user.reformatCommonId(commonId);
    });
    return props.user.dealerGroup.filter((d) => commonIds.includes(d.commonId));
  }, [props]);

  const getDealerList = useCallback(() => {
    const dealers = [];
    let dealerIds = props.user.getCommonIdsWithoutSDLR();
    if (dealerIds) {
      dealerIds = dealerIds.split(",");
      dealerIds.forEach((dealer) => {
        const formattedDealer = props.user.reformatCommonId(dealer);
        dealers.push(
          props.user.dealerGroupingsMap[formattedDealer].completeDealerId
        );
      });
    }
    return dealers;
  }, [props]);

  const refreshTnCActionStatus = useCallback(() => {
    const status = {};
    const actioned = [];
    const notActioned = [];
    dealerList.forEach((dealer) => {
      const nextDealer = { ...dealer, dealerName: dealer.name };
      if (props.termsAndConditionsHistory[0].acceptedDealers.includes(dealer.commonId)) {
        nextDealer.termsStatus = props.t("PendingActionPrompt.accepted");
        actioned.push(nextDealer);
        status[dealer.commonId] = props.t("ConfirmDialog.accepted");
      } else if (
        props.termsAndConditionsHistory[0].declinedDealers.includes(dealer.commonId)
      ) {
        nextDealer.termsStatus = props.t("PendingActionPrompt.declined");
        actioned.push(nextDealer);
        status[dealer.commonId] = props.t("ConfirmDialog.rejected");
      } else {
        notActioned.push(nextDealer);
      }
    });
    setDealersActioned(actioned);
    setDealersNotActioned(notActioned);
    setTnCStatus(status);
  }, [dealerList, props]);

  useEffect(() => {
    refreshTnCActionStatus();
  }, [refreshTnCActionStatus]);

  const isMainViewingAll = useCallback(
    () =>
      props.user.isLoggedInUserMDLR &&
      onboardedDealerGroup.length > 1 &&
      props.user.showAll,
    [onboardedDealerGroup.length, props]
  );

  const isMainViewingBranch = useCallback(
    () =>
      props.user.isLoggedInUserMDLR &&
      onboardedDealerGroup.length > 1 &&
      !props.user.showAll,
    [onboardedDealerGroup.length, props]
  );

  const getCondition = useCallback(() => {
    const isMainViewingSDLR = props.isMainViewingSDLR(props.user);
    if (isMainViewingSDLR) return false;

    const doesPAOnlyForSDLR = () => {
      let pendingAction;
      if (props.user?.pendingActions) {
        pendingAction = props.user.pendingActions.find(
          (action) => action.actionName === TERMS_AND_CONDITIONS
        );
      }
      if (!pendingAction) return false;
      let allRemainingSDLR = true;
      const dealerIdList = pendingAction.attributes.dealerIds;
      dealerIdList.forEach((dealerId) => {
        if (props.user.dealerGroupingsMapByDealerId[dealerId].dealerType !== "SDLR") {
          allRemainingSDLR = false;
        }
      });
      return allRemainingSDLR;
    };

    return !(props.user.isLoggedInUserMDLR && props.user.showAll && doesPAOnlyForSDLR());
  }, [props]);

  const handleConfirmation = useCallback(
    async (tnc, acceptance, commonIds, dealerIds) => {
      props.hideOrShow(true);
      try {
        await TermsAndConditionsClient.saveDealerTermsAndConditionsAcceptance(
          props.user,
          tnc,
          acceptance,
          commonIds.toString().split("|").join("%7C")
        );
        if (acceptance) {
          await PendingActionClient.removeDealerPendingAction(
            props.user,
            dealerIds,
            TERMS_AND_CONDITIONS
          );
          await props.getPendingActions();
        }
        props.alterComponentView(tnc);
      } catch (error) {
        // Preserve flow while making failure observable.
        console.error("Failed to save terms and conditions action", error);
      } finally {
        if (props.user.dealerGroupingsMap) {
          props.user.updateUnOnboardedDealers(getDealerList(), TERMS_AND_CONDITIONS);
        }
        props.hideOrShow(false);
      }
    },
    [getDealerList, props]
  );

  const openDialog = useCallback(
    (acceptance) => {
      if (isMainViewingAll()) {
        acceptance ? setOpenConfirmPAPrompt(true) : setOpenDeclinePAPrompt(true);
      } else if (isMainViewingBranch()) {
        acceptance ? setOpenConfirmDialog(true) : setOpenDeclineDialog(true);
      } else if (acceptance) {
        handleConfirmation(
          termsAndConditions,
          true,
          props.user.getCommonIdsWithoutSDLR(),
          props.user.getDealerIdsWithoutSDLR()
        );
      } else {
        setOpenDeclineDialog(true);
      }
    },
    [handleConfirmation, isMainViewingAll, isMainViewingBranch, props, termsAndConditions]
  );

  const applyAll = useCallback(
    (tnc, acceptance) => {
      const commonIds = [];
      const dealerIds = [];
      dealerList.forEach((dealer) => {
        commonIds.push(dealer.commonId);
        dealerIds.push(dealer.completeDealerId);
      });
      handleConfirmation(tnc, acceptance, commonIds, dealerIds);
    },
    [dealerList, handleConfirmation]
  );

  const applySelective = useCallback(
    (tnc, acceptance) => {
      const commonIds = [];
      const dealerIds = [];
      dealerList.forEach((dealer) => {
        if (!dealersActioned.some((d) => d.commonId === dealer.commonId)) {
          commonIds.push(dealer.commonId);
          dealerIds.push(dealer.completeDealerId);
        }
      });
      handleConfirmation(tnc, acceptance, commonIds, dealerIds);
    },
    [dealerList, dealersActioned, handleConfirmation]
  );

  return (
    <Box component="section" sx={{ py: 4, px: { xs: 4, sm: 4, md: 4, lg: 14 } }}>
      <div>
        <EMPButton
          variant="text"
          buttonElement="button"
          leftIcon={<MotionChevronLeft />}
          onClick={() => props.alterComponentView(termsAndConditions)}
        >
          {props.fromPending
            ? props.t("BusinessSettings.backToDashboard")
            : props.t("BusinessSettings.backToEmp")}
        </EMPButton>

        <div className={styles.termsConditionsHeader}>
          {props.t("BusinessSettings.termsTitle")}
        </div>
        <div className={styles.previewPDFWrapper}>
          <div className={styles.previewPDFTitle}>
            {props.user.market.toUpperCase() +
              " " +
              props.t("BusinessSettings.termsHeader") +
              termsAndConditions.version +
              ".0"}
          </div>
          <div className={styles.effectiveFrom}>
            {" "}
            {props.t("BusinessSettings.termsEffectiveDate")}{" "}
            {termsAndConditions.effectiveDate})
          </div>
          <PreviewTermsConditions
            className={styles.previewPDF}
            file={termsAndConditions.file}
          />
        </div>
        <div className={styles.downloadNewVersion}>
          <button
            type="button"
            className={styles.downloadNewVersionButton}
            onClick={() => props.handleTermsAndConditionsDownload(termsAndConditions)}
          >
            {props.t("BusinessSettings.downloadTerms")}
          </button>
        </div>
      </div>

      <PendingActionPrompt
        {...props}
        disabled={false}
        customText={customText}
        showDeclineWarning={true}
        dealersActioned={dealersActioned}
        dealersNotActioned={dealersNotActioned}
        title={props.t("BusinessSettings.termsTitle")}
        buttonTitle={props.t("BusinessSettings.termsTitle")}
        onClose={() => setOpenDeclinePAPrompt(false)}
        open={openDeclinePAPrompt}
        applyAll={() => applyAll(termsAndConditions, false)}
        applySome={() => applySelective(termsAndConditions, false)}
      />
      <PendingActionPrompt
        {...props}
        disabled={false}
        customText={customText}
        dealersActioned={dealersActioned}
        dealersNotActioned={dealersNotActioned}
        title={props.t("BusinessSettings.termsTitle")}
        buttonTitle={props.t("BusinessSettings.termsTitle")}
        onClose={() => setOpenConfirmPAPrompt(false)}
        open={openConfirmPAPrompt}
        applyAll={() => applyAll(termsAndConditions, true)}
        applySome={() => applySelective(termsAndConditions, true)}
      />

      <ConfirmDialog
        {...props}
        dialogProps={deactivateDialogProps}
        showWarning={props.user.isLoggedInUserMDLR}
        dealerList={dealerList}
        open={openDeclineDialog}
        onConfirm={() =>
          handleConfirmation(
            termsAndConditions,
            false,
            props.user.getCommonIdsWithoutSDLR(),
            props.user.getDealerIdsWithoutSDLR()
          )
        }
        onCancel={() => setOpenDeclineDialog(false)}
        onClose={() => setOpenDeclineDialog(false)}
        attributes={tnCStatus}
        boldTitle={false}
      />
      <ConfirmDialog
        {...props}
        dialogProps={confirmDialogProps}
        showWarning={true}
        dealerList={dealerList}
        open={openConfirmDialog}
        onConfirm={() =>
          handleConfirmation(
            termsAndConditions,
            true,
            props.user.getCommonIdsWithoutSDLR(),
            props.user.getDealerIdsWithoutSDLR()
          )
        }
        onCancel={() => setOpenConfirmDialog(false)}
        onClose={() => setOpenConfirmDialog(false)}
        attributes={tnCStatus}
        boldTitle={false}
      />

      <HasPermissionTo
        perform={["acceptTermsAndConditions"]}
        permissions={props.user.permissions.rolePermissions}
        condition={getCondition()}
        render={() => (
          <>
            <div
              className={`${styles.acceptanceSelector} pagePaddingTop pagePaddingBottom primary-text-bold-color`}
            >
              {props.t("BusinessSettings.termsAccept")}
            </div>

            <Grid item xs={12} container justifyContent="center">
              <Box>
                <EMPButton
                  variant="text"
                  buttonElement="button"
                  onClick={() =>
                    IS_CANADA_MARKET(props.user.market)
                      ? props.alterComponentView(termsAndConditions)
                      : openDialog(false)
                  }
                  disabled={UpperCaseText(props.user.userType) === "FIELD_VIEW"}
                >
                  {props.t("BusinessSettings.decline")}
                </EMPButton>
              </Box>
              <Box ml={2}>
                <EMPButton
                  onClick={() => openDialog(true)}
                  disabled={UpperCaseText(props.user.userType) === "FIELD_VIEW"}
                  variant="contained"
                  buttonElement="button"
                >
                  {props.t("BusinessSettings.accept")}
                </EMPButton>
              </Box>
            </Grid>
          </>
        )}
      />
    </Box>
  );
};

AcceptTermsAndConditions.propTypes = {
  alterComponentView: PropTypes.func.isRequired,
  fromPending: PropTypes.bool,
  getPendingActions: PropTypes.func.isRequired,
  handleTermsAndConditionsDownload: PropTypes.func.isRequired,
  hideOrShow: PropTypes.func.isRequired,
  isMainViewingSDLR: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  termsAndConditions: PropTypes.shape({
    effectiveDate: PropTypes.string,
    file: PropTypes.any,
    version: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  termsAndConditionsHistory: PropTypes.arrayOf(
    PropTypes.shape({
      acceptedDealers: PropTypes.arrayOf(PropTypes.string),
      declinedDealers: PropTypes.arrayOf(PropTypes.string),
      effectiveDate: PropTypes.string,
      file: PropTypes.any,
      version: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  user: PropTypes.shape({
    dealerGroup: PropTypes.arrayOf(
      PropTypes.shape({
        commonId: PropTypes.string,
        completeDealerId: PropTypes.string,
        name: PropTypes.string,
      })
    ),
    dealerGroupingsMap: PropTypes.object,
    dealerGroupingsMapByDealerId: PropTypes.object,
    getCommonIdsWithoutSDLR: PropTypes.func.isRequired,
    getDealerIdsWithoutSDLR: PropTypes.func.isRequired,
    getOnboardedDealerGroup: PropTypes.func.isRequired,
    isLoggedInUserMDLR: PropTypes.bool,
    market: PropTypes.string,
    pendingActions: PropTypes.arrayOf(
      PropTypes.shape({
        actionName: PropTypes.string,
        attributes: PropTypes.shape({
          dealerIds: PropTypes.arrayOf(PropTypes.string),
        }),
      })
    ),
    permissions: PropTypes.shape({
      rolePermissions: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    }),
    reformatCommonId: PropTypes.func.isRequired,
    showAll: PropTypes.bool,
    updateUnOnboardedDealers: PropTypes.func,
    userType: PropTypes.string,
  }).isRequired,
};

export default withTranslation("emp")(AcceptTermsAndConditions);
